import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { BookmarkPlus } from "lucide-react";
import { toast } from "sonner";

// Bouton raccourci "Enregistrer au carnet" : ouvre une mini-fiche pré-remplie
// avec l'adresse (et éventuellement lat/lon) du champ courant. Ne navigue jamais
// hors de la page Jeregul.
export function SaveAsPlaceButton({ address, lat, lon, defaultName, testid = "save-as-place", size = "sm" }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState({ types: [], specialites: [] });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", address: "", phone: "", place_type: "Autre", specialite: "",
    praticien: "", floor: "", access_code: "", hours: "", notes: "",
  });

  useEffect(() => {
    if (!open) return;
    api.get("/places/meta").then(r => setMeta(r.data)).catch(() => {});
    const seedName = (defaultName || (address || "").split(",")[0] || "").trim().slice(0, 80);
    setForm(f => ({ ...f, name: seedName, address: address || "" }));
  }, [open, address, defaultName]);

  const disabled = !address || !address.trim();

  const save = async () => {
    if (!form.name.trim() || !form.address.trim()) { toast.error("Nom et adresse requis"); return; }
    setSaving(true);
    try {
      await api.post("/places", { ...form, lat: lat ?? null, lon: lon ?? null });
      toast.success("Lieu ajouté à votre annuaire");
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size={size}
        disabled={disabled}
        onClick={() => setOpen(true)}
        title={disabled ? "Renseignez d'abord une adresse" : "Enregistrer au carnet d'adresses"}
        className="h-8 px-2 text-xs"
        data-testid={testid}
      >
        <BookmarkPlus className="h-3.5 w-3.5 mr-1" /> Au carnet
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle>Enregistrer au carnet d'adresses</DialogTitle>
            <DialogDescription>Complétez la fiche — vous la retrouverez dans l'Annuaire médical et dans l'autocomplétion.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Nom du lieu *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="CH Dijon Bocage - Entrée Urgences" data-testid={`${testid}-name`} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Adresse *</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid={`${testid}-address`} />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.place_type} onValueChange={(v) => setForm({ ...form, place_type: v })}>
                <SelectTrigger data-testid={`${testid}-type`}><SelectValue /></SelectTrigger>
                <SelectContent>{(meta.types || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Spécialité</Label>
              <Select value={form.specialite || "__none__"} onValueChange={(v) => setForm({ ...form, specialite: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid={`${testid}-spec`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {(meta.specialites || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Téléphone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="03 80 XX XX XX" data-testid={`${testid}-phone`} />
            </div>
            <div>
              <Label className="text-xs">Praticien référent</Label>
              <Input value={form.praticien} onChange={(e) => setForm({ ...form, praticien: e.target.value })} placeholder="Dr Martin" data-testid={`${testid}-praticien`} />
            </div>
            <div>
              <Label className="text-xs">Étage / Bâtiment / Service</Label>
              <Input value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} placeholder="Bât. B - 3ᵉ étage - Dialyse" data-testid={`${testid}-floor`} />
            </div>
            <div>
              <Label className="text-xs">Code d'accès</Label>
              <Input value={form.access_code} onChange={(e) => setForm({ ...form, access_code: e.target.value })} data-testid={`${testid}-code`} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Horaires</Label>
              <Input value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="Lun-Ven 8h-19h" data-testid={`${testid}-hours`} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Remarques</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Parking dépose-minute. Sonner à l'interphone." data-testid={`${testid}-notes`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid={`${testid}-cancel`}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid={`${testid}-save`}>{saving ? "Enregistrement…" : "Enregistrer au carnet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
